import { z } from 'zod';
import { CORPORATE_INVOICE_STATUSES, CORPORATE_QUOTE_STATUSES, MEMBER_STATUSES, MEMBERSHIP_STATUSES, PAYMENT_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema, localeSchema, pageSchema, patchOf, searchQuerySchema } from './common';
import { createMemberSchema } from './members';

export interface CorporateLineAmounts {
  subtotalMinor: number;
  discountMinor: number;
  totalMinor: number;
}

/** A quote or invoice line in minor units: quantity × unit price, less the discount rounded to the nearest minor unit, half up. */
export function corporateLineAmounts(quantity: number, unitPriceMinor: number, discountPct: number): CorporateLineAmounts {
  const subtotalMinor = quantity * unitPriceMinor;
  const discountMinor = Math.floor((subtotalMinor * discountPct + 50) / 100);
  return { subtotalMinor, discountMinor, totalMinor: subtotalMinor - discountMinor };
}

/** Splits an amount into shares that differ by at most one minor unit and add up to it exactly; the first shares take the remainder. */
export function splitEvenly(totalMinor: number, parts: number): number[] {
  const base = Math.floor(totalMinor / parts);
  const rest = totalMinor - base * parts;
  return Array.from({ length: parts }, (_, i) => base + (i < rest ? 1 : 0));
}

const textOrNull = (max: number) => z.string().trim().max(max).nullable().optional();
const discountPctSchema = z.number().int().min(0).max(99).describe('Whole percent off the unit price, 0–99; a corporate sale is never free');

export const CORPORATE_CLIENT_FILTERS = ['ACTIVE', 'ARCHIVED', 'ALL'] as const;
export type CorporateClientFilter = (typeof CORPORATE_CLIENT_FILTERS)[number];

export const createCorporateClientSchema = z.object({
  name: z.string().trim().min(1).max(160),
  taxId: textOrNull(40).describe('The company’s identification code; unique within the gym'),
  address: textOrNull(300),
  contactName: textOrNull(120),
  email: z.email().toLowerCase().nullable().optional().describe('Where quotes are emailed'),
  phone: textOrNull(32),
  billingTermsDays: z.number().int().min(0).max(365).default(14).describe('Days from issue to the due date of its invoices'),
  discountPct: discountPctSchema.default(0).describe('The discount every new quote line starts with'),
  notes: textOrNull(2000),
  active: z.boolean().default(true).describe('Off archives the client: it stays on its quotes and invoices and leaves the pickers'),
});
export type CreateCorporateClientInput = z.infer<typeof createCorporateClientSchema>;
export const updateCorporateClientSchema = patchOf(createCorporateClientSchema);
export type UpdateCorporateClientInput = z.infer<typeof updateCorporateClientSchema>;

export const corporateClientListQuerySchema = searchQuerySchema.extend({
  status: z.enum(CORPORATE_CLIENT_FILTERS).default('ACTIVE'),
});
export type CorporateClientListQuery = z.infer<typeof corporateClientListQuerySchema>;

export const corporateClientSchema = z.object({
  id: idSchema,
  name: z.string(),
  taxId: z.string().nullable(),
  address: z.string().nullable(),
  contactName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  billingTermsDays: z.number().int(),
  discountPct: z.number().int(),
  notes: z.string().nullable(),
  active: z.boolean(),
  rosterCount: z.number().int(),
  outstandingMinor: z.number().int().describe('What its issued, unpaid invoices add up to'),
  overdueMinor: z.number().int().describe('The part of it past the due date'),
  currency: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CorporateClientDto = z.infer<typeof corporateClientSchema>;
export const corporateClientListSchema = pageSchema(corporateClientSchema);
export type CorporateClientListDto = z.infer<typeof corporateClientListSchema>;

export const corporateRosterMemberSchema = z.object({
  memberId: idSchema,
  memberNumber: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.enum(MEMBER_STATUSES),
  membership: z
    .object({
      id: idSchema,
      planName: z.string(),
      status: z.enum(MEMBERSHIP_STATUSES),
      startsAt: z.string().nullable(),
      endsAt: z.string().nullable(),
      paidByClient: z.boolean().describe('Bought through one of this client’s invoices'),
    })
    .nullable()
    .describe('The membership that matters now: the one in use, else the latest'),
});
export type CorporateRosterMemberDto = z.infer<typeof corporateRosterMemberSchema>;

export const addCorporateRosterMemberSchema = z.union([
  z.object({ memberId: idSchema.describe('A member of the gym who is on no company’s roster') }),
  z.object({ member: createMemberSchema.describe('A new member, made as the desk makes one (members.create)') }),
]);
export type AddCorporateRosterMemberInput = z.infer<typeof addCorporateRosterMemberSchema>;

export const corporateLineInputSchema = z.object({
  planId: idSchema,
  quantity: z.number().int().min(1).max(1000).describe('How many people'),
  unitPriceMinor: z.number().int().min(0).max(100_000_000).optional().describe("Per person before the discount; the plan's price when left out"),
  discountPct: discountPctSchema.optional().describe("The client's discount when left out"),
});
export type CorporateLineInput = z.infer<typeof corporateLineInputSchema>;

export const createCorporateQuoteSchema = z.object({
  clientId: idSchema,
  validUntil: dateOnlySchema.optional().describe('The last day it can be accepted; 30 days from today when left out'),
  locale: localeSchema.optional().describe("The language of its PDF and email; the gym's default when left out"),
  notes: textOrNull(2000),
  soldByUserId: idSchema.nullable().optional().describe('The staff member credited with the sale (OBO-714); the caller when left out'),
  lines: z.array(corporateLineInputSchema).min(1).max(20),
});
export type CreateCorporateQuoteInput = z.infer<typeof createCorporateQuoteSchema>;
export const updateCorporateQuoteSchema = patchOf(createCorporateQuoteSchema.omit({ clientId: true }));
export type UpdateCorporateQuoteInput = z.infer<typeof updateCorporateQuoteSchema>;

export const corporateQuoteListQuerySchema = searchQuerySchema.extend({
  status: z.enum(CORPORATE_QUOTE_STATUSES).optional(),
  clientId: idSchema.optional(),
});
export type CorporateQuoteListQuery = z.infer<typeof corporateQuoteListQuerySchema>;

export const corporateLineSchema = z.object({
  id: idSchema,
  planId: idSchema,
  description: z.string(),
  descriptionLocalized: z.record(z.string(), z.string()),
  quantity: z.number().int(),
  unitPriceMinor: z.number().int(),
  discountPct: z.number().int(),
  subtotalMinor: z.number().int().describe('quantity × unit price'),
  discountMinor: z.number().int().describe('The discount, rounded to the nearest minor unit, half up'),
  totalMinor: z.number().int().describe('subtotal − discount'),
});
export type CorporateLineDto = z.infer<typeof corporateLineSchema>;

export const corporateQuoteSchema = z.object({
  id: idSchema,
  number: z.string(),
  clientId: idSchema,
  clientName: z.string(),
  status: z.enum(CORPORATE_QUOTE_STATUSES),
  locale: localeSchema,
  validUntil: z.string(),
  expired: z.boolean().describe("Past its last day on the gym's calendar; it can no longer be accepted"),
  currency: z.string(),
  notes: z.string().nullable(),
  soldByUserId: idSchema.nullable(),
  soldByName: z.string().nullable(),
  sentAt: z.string().nullable(),
  decidedAt: z.string().nullable(),
  lines: z.array(corporateLineSchema),
  subtotalMinor: z.number().int(),
  discountMinor: z.number().int(),
  totalMinor: z.number().int(),
  invoiceId: idSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CorporateQuoteDto = z.infer<typeof corporateQuoteSchema>;
export const corporateQuoteListSchema = pageSchema(corporateQuoteSchema);
export type CorporateQuoteListDto = z.infer<typeof corporateQuoteListSchema>;

export const sendCorporateQuoteResultSchema = corporateQuoteSchema.extend({
  emailQueued: z.boolean().describe('False when the client has no email: the quote is marked sent and its PDF is for the staff to pass on'),
});
export type SendCorporateQuoteResultDto = z.infer<typeof sendCorporateQuoteResultSchema>;

export const acceptCorporateQuoteSchema = z.object({
  lines: z
    .array(z.object({ lineId: idSchema, memberIds: z.array(idSchema).min(1).max(1000).describe("Exactly the line's quantity of people from the client's roster") }))
    .min(1)
    .max(20),
  startsOn: dateOnlySchema.optional().describe("The day the memberships start once the invoice is paid, today or later on the gym's calendar; left out, they start on the day the payment is recorded. A payment recorded after this day starts them that day"),
  emailInvoice: z.boolean().default(true).describe("Email the invoice to the client's address with a link to download it; nothing is sent when the client has no address"),
});
export type AcceptCorporateQuoteInput = z.infer<typeof acceptCorporateQuoteSchema>;

export const declineCorporateQuoteSchema = z.object({ reason: textOrNull(500) });
export type DeclineCorporateQuoteInput = z.infer<typeof declineCorporateQuoteSchema>;

export const corporateInvoiceMemberSchema = z.object({
  memberId: idSchema,
  memberNumber: z.string(),
  name: z.string(),
  membershipId: idSchema.nullable(),
  membershipStatus: z.enum(MEMBERSHIP_STATUSES).nullable(),
  paymentId: idSchema,
  paymentStatus: z.enum(PAYMENT_STATUSES),
  amountMinor: z.number().int().describe("This person's share of the line"),
  refundedMinor: z.number().int(),
});
export type CorporateInvoiceMemberDto = z.infer<typeof corporateInvoiceMemberSchema>;

export const corporateInvoiceSummarySchema = z.object({
  id: idSchema,
  number: z.string(),
  clientId: idSchema,
  clientName: z.string(),
  quoteId: idSchema.nullable(),
  quoteNumber: z.string().nullable(),
  status: z.enum(CORPORATE_INVOICE_STATUSES),
  locale: localeSchema,
  issuedOn: z.string(),
  dueOn: z.string(),
  overdue: z.boolean().describe("Issued, unpaid and past its due date on the gym's calendar"),
  currency: z.string(),
  totalMinor: z.number().int(),
  soldByUserId: idSchema.nullable(),
  soldByName: z.string().nullable(),
  paidAt: z.string().nullable(),
  paidOn: z.string().nullable(),
  paymentReference: z.string().nullable(),
  paidByName: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  voidedByName: z.string().nullable(),
  startsOn: z.string().nullable().describe('The day the memberships start once paid; null for the payment day'),
  emailedTo: z.string().nullable().describe('Where the invoice was emailed when it was issued; null when it was not'),
  emailedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CorporateInvoiceSummaryDto = z.infer<typeof corporateInvoiceSummarySchema>;

export const corporateInvoiceSchema = corporateInvoiceSummarySchema.extend({
  lines: z.array(corporateLineSchema.extend({ members: z.array(corporateInvoiceMemberSchema) })),
  refundedMinor: z.number().int().describe('Refunded so far through the payments it paid for'),
});
export type CorporateInvoiceDto = z.infer<typeof corporateInvoiceSchema>;

export const corporateInvoiceListQuerySchema = searchQuerySchema.extend({
  status: z.enum(CORPORATE_INVOICE_STATUSES).optional(),
  clientId: idSchema.optional(),
  overdue: z.stringbool().optional().describe('Only issued invoices past their due date'),
});
export type CorporateInvoiceListQuery = z.infer<typeof corporateInvoiceListQuerySchema>;

export const corporateInvoiceListSchema = pageSchema(corporateInvoiceSummarySchema).extend({
  totals: z.object({
    outstandingMinor: z.number().int().describe('Every issued, unpaid invoice of the gym'),
    overdueMinor: z.number().int(),
    overdueCount: z.number().int(),
    currency: z.string(),
  }),
});
export type CorporateInvoiceListDto = z.infer<typeof corporateInvoiceListSchema>;

export const payCorporateInvoiceSchema = z.object({
  paidOn: dateOnlySchema.optional().describe("The day the transfer reached the gym's account; today when left out, never in the future"),
  reference: textOrNull(120).describe('The bank’s reference for the transfer'),
});
export type PayCorporateInvoiceInput = z.infer<typeof payCorporateInvoiceSchema>;

export const voidCorporateInvoiceSchema = z.object({ reason: z.string().trim().min(1).max(500) });
export type VoidCorporateInvoiceInput = z.infer<typeof voidCorporateInvoiceSchema>;

export const corporateDocumentQuerySchema = z.object({ locale: localeSchema.optional().describe("The document's own language when left out") });
export type CorporateDocumentQuery = z.infer<typeof corporateDocumentQuerySchema>;
